import React, { useEffect, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import { useIsSidebarOpen } from '../app/layout/IsSidebarOpenContext';
import useAppSelector from '../app/redux/useAppSelector';
import { usePostTOC } from './PostTOCContext';

const useOnMainEditorResize = (handler: () => void) => {
  const { isTOCOpen } = usePostTOC();
  const didMountRef = useRef(false);
  const handlerRef = useRef(handler);

  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  const isRightPanelOpen = useAppSelector(
    (state) => state.ui.rightPanel.isOpen,
  );
  const [isSidebarOpen] = useDebounce(
    useIsSidebarOpen(),
    // Sync with #sidebar's transition duration
    150,
  );

  useEffect(() => {
    if (didMountRef.current) {
      handlerRef.current();
    } else {
      didMountRef.current = true;
    }
  }, [isRightPanelOpen, isSidebarOpen, isTOCOpen]);

  useEffect(() => {
    const listener = () => handlerRef.current();
    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
    };
  }, []);
};

interface MainEditorResizeHandlerProps {
  onMainEditorResize: () => void;
  children: React.ReactNode;
}

export const MainEditorResizeHandler = ({
  onMainEditorResize,
  children,
}: MainEditorResizeHandlerProps) => {
  useOnMainEditorResize(onMainEditorResize);
  return <>{children}</>;
};

export default useOnMainEditorResize;
